/**
 * One frame of the composition, drawn once.
 *
 * This is the single most important function in the app, because preview and
 * export both call it. The picture someone arranges while scrubbing and the
 * picture written into the file come out of the same arithmetic, so what they
 * see is what they get — there is no second layout path to keep in step.
 *
 * It is deliberately synchronous and side-effect free: give it a context, a
 * project, a time, and a way to look up a drawable per source, and it paints.
 * Deciding *which* element is ready at that time is the caller's job, and
 * preview and export answer it very differently.
 *
 * Three kinds of thing get drawn, bottom to top: video (and still images, which
 * are video clips over a source that happens not to move), then text, which
 * has no source at all and is laid out from the words in the clip.
 */

import { animationAt } from "./animation.ts";
import { layoutText, loadFont, type Measure } from "./text.ts";
import {
  clipBoxHeight,
  sourceById,
  sourceTimeAt,
  visualClipsAt,
  type Clip,
  type Project,
  type SourceRef,
} from "./timeline.ts";

/** Anything the canvas can paint a source with. */
export type Drawable = HTMLVideoElement | HTMLImageElement | ImageBitmap;

/** A source's current picture, or null when we have no file for it. */
export type FrameLookup = (sourceId: string) => Drawable | null;

export interface RenderOptions {
  project: Project;
  t: number;
  get: FrameLookup;
  /** Draws the placeholder for clips whose file is missing. On by default. */
  placeholders?: boolean;
}

const BACKGROUND = "#14100c";
const PLACEHOLDER_FILL = "#2b2118";
const PLACEHOLDER_EDGE = "#6b5744";
const PLACEHOLDER_TEXT = "#c8b79f";

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function renderFrame(ctx: CanvasRenderingContext2D, options: RenderOptions): void {
  const { project, t, get, placeholders = true } = options;
  const { width, height } = ctx.canvas;

  ctx.save();
  ctx.fillStyle = BACKGROUND;
  ctx.fillRect(0, 0, width, height);

  // The composition is authored at project.width/height; the canvas may be any
  // size. Scaling once here is what lets every transform below stay normalised.
  const scale = Math.min(width / project.width, height / project.height);
  const offsetX = (width - project.width * scale) / 2;
  const offsetY = (height - project.height * scale) / 2;
  ctx.translate(offsetX, offsetY);
  ctx.scale(scale, scale);

  for (const clip of visualClipsAt(project, t)) {
    if (clip.kind === "text") {
      drawText(ctx, project, clip, t);
      continue;
    }
    const source = sourceById(project, clip.sourceId);
    if (!source) continue;
    const element = get(clip.sourceId);
    if (element) drawClip(ctx, project, clip, source, element, t);
    else if (placeholders) drawPlaceholder(ctx, project, clip, source, t);
  }

  ctx.restore();
}

/**
 * The box a clip occupies, in composition coordinates.
 *
 * Its height comes from the transform when the aspect ratio has been unlocked,
 * and from the source's own shape when it has not — so a locked clip cannot
 * drift out of proportion no matter what else is edited.
 */
export function clipRect(project: Project, clip: Clip, source: SourceRef | undefined): Rect {
  const w = project.width * clip.transform.scale;
  const h = project.height * clipBoxHeight(project, clip, source);
  return {
    x: clip.transform.x * project.width - w / 2,
    y: clip.transform.y * project.height - h / 2,
    w,
    h,
  };
}

/**
 * Rotation, opacity, corner rounding and the entrance or exit in progress,
 * applied around `paint`. The animation is a change to the same three things
 * the transform already sets — where, how big, how solid — so it lives here
 * rather than in each painter.
 */
function withTransform(
  ctx: CanvasRenderingContext2D,
  project: Project,
  clip: Clip,
  source: SourceRef | undefined,
  t: number,
  paint: (rect: Rect) => void,
): void {
  const rect = clipRect(project, clip, source);
  const motion = animationAt(clip, t);
  ctx.save();
  ctx.globalAlpha = Math.max(0, Math.min(1, clip.transform.opacity * motion.alpha));

  const cx = rect.x + rect.w / 2;
  const cy = rect.y + rect.h / 2;
  if (motion.dx !== 0 || motion.dy !== 0) {
    ctx.translate(motion.dx * project.width, motion.dy * project.height);
  }
  if (motion.scale !== 1) {
    ctx.translate(cx, cy);
    ctx.scale(motion.scale, motion.scale);
    ctx.translate(-cx, -cy);
  }
  if (clip.transform.rotation !== 0) {
    ctx.translate(cx, cy);
    ctx.rotate((clip.transform.rotation * Math.PI) / 180);
    ctx.translate(-cx, -cy);
  }

  // A radius of 0.5 on the shorter side is a circle, which is the shape every
  // reaction-cam overlay on the internet uses. Text rounds its background
  // instead, so it is not clipped here.
  const radius = clip.kind === "text" ? 0 : clip.transform.radius * Math.min(rect.w, rect.h);
  if (radius > 0) {
    ctx.beginPath();
    ctx.roundRect(rect.x, rect.y, rect.w, rect.h, radius);
    ctx.clip();
  }

  paint(rect);
  ctx.restore();
}

/** Natural pixel size of whatever we were handed; zero while a video has nothing decoded. */
function sizeOf(element: Drawable): { w: number; h: number } {
  if (element instanceof HTMLVideoElement) return { w: element.videoWidth, h: element.videoHeight };
  if (element instanceof HTMLImageElement) return { w: element.naturalWidth, h: element.naturalHeight };
  return { w: element.width, h: element.height };
}

function drawClip(
  ctx: CanvasRenderingContext2D,
  project: Project,
  clip: Clip,
  source: SourceRef,
  element: Drawable,
  t: number,
): void {
  // An element that has not decoded anything *yet* draws as a black box on some
  // browsers and throws on others, so skip it and let the next frame try. Once
  // it has decoded something it stays drawable, including mid-seek, which is
  // what keeps a paused clip on screen instead of flashing black.
  if (element instanceof HTMLVideoElement) {
    if (element.readyState < 2 && element.currentTime === 0 && !element.duration) return;
  }
  if (element instanceof HTMLImageElement && !element.complete) return;
  const size = sizeOf(element);
  if (size.w === 0 || size.h === 0) return;
  withTransform(ctx, project, clip, source, t, (rect) => {
    paintFitted(ctx, element, size, clip, rect);
  });
}

/**
 * Put the picture in the box according to the clip's fit mode.
 *
 * With the aspect ratio locked the box already matches the source and all three
 * modes draw the same thing, so this only earns its keep once someone has sized
 * a clip freely. Then the choice is real: stretch distorts, cover fills and
 * loses the edges, contain keeps everything and leaves gaps.
 */
function paintFitted(
  ctx: CanvasRenderingContext2D,
  element: Drawable,
  size: { w: number; h: number },
  clip: Clip,
  rect: Rect,
): void {
  const sw = size.w;
  const sh = size.h;
  const sourceAspect = sw / sh;
  const boxAspect = rect.w / rect.h;

  if (clip.transform.fit === "stretch" || Math.abs(sourceAspect - boxAspect) < 0.001) {
    ctx.drawImage(element, rect.x, rect.y, rect.w, rect.h);
    return;
  }

  if (clip.transform.fit === "cover") {
    // Crop the source to the box's shape, centred, then fill the box with it.
    const cropW = sourceAspect > boxAspect ? sh * boxAspect : sw;
    const cropH = sourceAspect > boxAspect ? sh : sw / boxAspect;
    ctx.drawImage(
      element,
      (sw - cropW) / 2,
      (sh - cropH) / 2,
      cropW,
      cropH,
      rect.x,
      rect.y,
      rect.w,
      rect.h,
    );
    return;
  }

  // contain: the whole picture, centred in the box, at its own proportions.
  const drawW = sourceAspect > boxAspect ? rect.w : rect.h * sourceAspect;
  const drawH = sourceAspect > boxAspect ? rect.w / sourceAspect : rect.h;
  ctx.drawImage(
    element,
    rect.x + (rect.w - drawW) / 2,
    rect.y + (rect.h - drawH) / 2,
    drawW,
    drawH,
  );
}

/**
 * A title.
 *
 * The box is the clip's transform; the words are wrapped into it at the
 * clip's font size and placed by the style's alignment. The background, when
 * there is one, is the whole box: making it hug the words instead would mean
 * the box on screen and the box in the handles were two different shapes.
 */
function drawText(ctx: CanvasRenderingContext2D, project: Project, clip: Clip, t: number): void {
  const text = clip.text;
  if (!text) return;
  const { style } = text;
  // Measured with the context we are about to draw with, so wrapping and
  // painting cannot disagree about a glyph's width.
  const measure: Measure = (line, font) => {
    ctx.font = font;
    return ctx.measureText(line).width;
  };
  void loadFont(style);

  withTransform(ctx, project, clip, undefined, t, (rect) => {
    const layout = layoutText(text.content, style, project, rect.w, measure);

    if (style.background && style.background.opacity > 0) {
      ctx.save();
      ctx.globalAlpha *= style.background.opacity;
      ctx.fillStyle = style.background.color;
      const radius = clip.transform.radius * Math.min(rect.w, rect.h);
      ctx.beginPath();
      ctx.roundRect(rect.x, rect.y, rect.w, rect.h, radius);
      ctx.fill();
      ctx.restore();
    }

    ctx.font = layout.font;
    ctx.textAlign = style.align;
    ctx.textBaseline = "middle";
    const x =
      style.align === "left"
        ? rect.x + layout.paddingPx
        : style.align === "right"
          ? rect.x + rect.w - layout.paddingPx
          : rect.x + rect.w / 2;
    const block = layout.lines.length * layout.lineHeightPx;
    const inner = rect.h - layout.paddingPx * 2;
    const top =
      style.valign === "top"
        ? rect.y + layout.paddingPx
        : style.valign === "bottom"
          ? rect.y + rect.h - layout.paddingPx - block
          : rect.y + layout.paddingPx + (inner - block) / 2;

    if (style.shadow) {
      ctx.shadowColor = "rgba(0, 0, 0, 0.55)";
      ctx.shadowBlur = layout.fontPx * 0.18;
      ctx.shadowOffsetY = layout.fontPx * 0.05;
    }
    ctx.lineJoin = "round";
    layout.lines.forEach((line, index) => {
      const y = top + index * layout.lineHeightPx + layout.lineHeightPx / 2;
      if (style.outline && style.outline.width > 0) {
        ctx.strokeStyle = style.outline.color;
        ctx.lineWidth = style.outline.width * layout.fontPx;
        ctx.strokeText(line, x, y);
      }
      ctx.fillStyle = style.color;
      ctx.fillText(line, x, y);
    });
  });
}

/**
 * A clip whose file we do not have.
 *
 * Drawn at full size with its name on it rather than skipped, because a
 * missing file is a state of the project and not an error: the edit is intact,
 * the person just has not handed the file back yet, and seeing the gap where it
 * belongs is how they know what to look for.
 */
function drawPlaceholder(
  ctx: CanvasRenderingContext2D,
  project: Project,
  clip: Clip,
  source: SourceRef,
  t: number,
): void {
  withTransform(ctx, project, clip, source, t, (rect) => {
    ctx.fillStyle = PLACEHOLDER_FILL;
    ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
    ctx.strokeStyle = PLACEHOLDER_EDGE;
    ctx.lineWidth = Math.max(2, project.width / 640);
    ctx.setLineDash([ctx.lineWidth * 4, ctx.lineWidth * 3]);
    ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);
    ctx.setLineDash([]);

    const size = Math.max(14, Math.min(rect.w / 18, project.height / 22));
    ctx.fillStyle = PLACEHOLDER_TEXT;
    ctx.font = `${size}px ui-sans-serif, system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(
      elide(source.name, Math.floor(rect.w / (size * 0.55))),
      rect.x + rect.w / 2,
      rect.y + rect.h / 2 - size * 0.7,
    );
    ctx.font = `${size * 0.8}px ui-sans-serif, system-ui, sans-serif`;
    ctx.fillText("file not loaded", rect.x + rect.w / 2, rect.y + rect.h / 2 + size * 0.7);
  });
}

function elide(text: string, max: number): string {
  if (max < 8 || text.length <= max) return text;
  return `${text.slice(0, Math.max(4, max - 7))}…${text.slice(-6)}`;
}

/** Which clip a click at composition coordinates hits: topmost first. */
export function clipAtPoint(
  project: Project,
  t: number,
  point: { x: number; y: number },
): Clip | null {
  const stack = visualClipsAt(project, t);
  for (let i = stack.length - 1; i >= 0; i -= 1) {
    const clip = stack[i];
    if (!clip) continue;
    const source = sourceById(project, clip.sourceId);
    if (clip.kind === "video" && !source) continue;
    const rect = clipRect(project, clip, source);
    if (
      point.x >= rect.x &&
      point.x <= rect.x + rect.w &&
      point.y >= rect.y &&
      point.y <= rect.y + rect.h
    ) {
      return clip;
    }
  }
  return null;
}

export { sourceTimeAt };
